import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getPaginatedPartModels,
  getPartModel,
  getPartModelsByIds,
  getPartModelsBySearch,
} from "@/api/resources/partModels/api";
import i18n from "@/providers/i18n";
import type { SearchOptionFilters } from "@/utils/api";
import {
  stringifyQueryPaginationFilters,
  stringifySearchOptionFilters,
  type QueryPaginationFilters,
} from "@/utils/pagination";

const entity = i18n.t("part-model");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("part-models");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const partModelsQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "partModels"],
  lists: () => [...partModelsQueryKeys.all(), "list"],
  listByIds: (ids?: string[]) => [...partModelsQueryKeys.lists(), "byIds", ids],
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...partModelsQueryKeys.lists(),
    "paginated",
    stringifyQueryPaginationFilters(queryKeyPaginationFilters),
  ],
  listBySearch: (searchOptionFilters: SearchOptionFilters) => [
    ...partModelsQueryKeys.lists(),
    "bySearch",
    stringifySearchOptionFilters(searchOptionFilters),
  ],
  byId: (id?: string) => [...partModelsQueryKeys.all(), "byId", id],
};
export const partModelsQueries = {
  listByIds: (ids?: string[]) =>
    queryOptions({
      queryKey: [...partModelsQueryKeys.listByIds(ids)],
      queryFn: () =>
        getPartModelsByIds({
          keyIn: "id",
          idsIn: ids,
        }),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...partModelsQueryKeys.listPaginated(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedPartModels(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listBySearch: (searchOptionFilters: SearchOptionFilters) =>
    queryOptions({
      queryKey: [...partModelsQueryKeys.listBySearch(searchOptionFilters)],
      queryFn: () => getPartModelsBySearch(searchOptionFilters),
      placeholderData: (prev) => prev, // ! this prevents flashing of results when using search in selectors
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...partModelsQueryKeys.byId(id)],
      queryFn: id ? () => getPartModel({ match: { id } }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
